import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  FileText,
  BarChart3,
  Users,
  Building2,
  AlertCircle,
  Printer,
  FileDown,
  Activity,
  Minus,
  Info,
  AlertOctagon,
} from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import {
  ROUTE_PATHS,
  formatCurrency,
  formatDate,
  formatPct,
  groupVociByCategoria,
  calcScostamento,
  type Analisi,
  type VoceSpesa,
  type AnalisiKPI,
  type StatoPatrimoniale,
  type AnalisiCondomino,
  type DettaglioSpesa,
  type Raccomandazione,
} from '@/lib/index';
import {
  PieChartCategorie,
  BarChartConfronto,
  BarChartScostamenti,
  BarChartSaldoCondomini,
} from '@/components/Charts';
import { KPICard } from '@/components/Cards';

/* ─────────────────────────────────────────────────────────────────────────
   NOTA RENDERER — parser universale per testo generato da Claude
   Gestisce: Markdown (##, **, -, *), titoli MAIUSCOLO, paragrafi misti
──────────────────────────────────────────────────────────────────────── */
function NotaRenderer({ nota }: { nota: string }) {
  // Pulizia profonda caratteri di escape ridondanti (generati da JSON di Claude)
  let clean = nota || '';
  // Decodifica escape multipli \\n → \n → newline reale
  clean = clean
    .replace(/\\\\n/g, '\n')  // \\n doppio
    .replace(/\\n/g, '\n')    // \n singolo
    .replace(/\\\\t/g, ' ')  // \\t
    .replace(/\\t/g, ' ')    // \t
    .replace(/\\\\'/g, "'")
    .replace(/\\'/g, "'")
    .replace(/\\\\"/g, '"')
    .replace(/\\"/g, '"')
    .trim();
  // Se la nota non ha newline reali ma ha \n letterali (stringa JSON mal deserializzata)
  if (!clean.includes('\n') && clean.includes('##')) {
    clean = clean.replace(/##/g, '\n##').trim();
  }

  // Divide in blocchi logici
  const lines = clean.split('\n');

  type Block =
    | { type: 'h1'; text: string }
    | { type: 'h2'; text: string }
    | { type: 'h3'; text: string }
    | { type: 'paragraph'; text: string }
    | { type: 'bullet'; text: string }
    | { type: 'spacer' };

  const blocks: Block[] = [];

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const trimmed = raw.trim();

    if (!trimmed) {
      if (blocks.length > 0 && blocks[blocks.length - 1].type !== 'spacer') {
        blocks.push({ type: 'spacer' });
      }
      continue;
    }

    // Titolo markdown # / ## / ###
    if (/^###\s+/.test(trimmed)) {
      blocks.push({ type: 'h3', text: trimmed.replace(/^###\s+/, '') });
      continue;
    }
    if (/^##\s+/.test(trimmed)) {
      blocks.push({ type: 'h2', text: trimmed.replace(/^##\s+/, '') });
      continue;
    }
    if (/^#\s+/.test(trimmed)) {
      blocks.push({ type: 'h1', text: trimmed.replace(/^#\s+/, '') });
      continue;
    }

    // Lista puntata markdown (- o * o •)
    if (/^[-*•]\s+/.test(trimmed)) {
      blocks.push({ type: 'bullet', text: trimmed.replace(/^[-*•]\s+/, '') });
      continue;
    }

    // Titolo ALL-CAPS (minimo 4 char maiuscoli, può finire con : o non)
    // Es: "PREMESSA", "ANALISI SPESE:", "CONCLUSIONI"
    if (
      /^[A-ZÀÈÌÒÙÉ][A-ZÀÈÌÒÙÉ\s\d'àèìòùé.,()-]{3,}[A-ZÀÈÌÒÙÉA-Za-z0-9)][:.]?$/.test(trimmed) &&
      trimmed === trimmed.toUpperCase() &&
      trimmed.length <= 80
    ) {
      blocks.push({ type: 'h2', text: trimmed.replace(/:$/, '') });
      continue;
    }

    // Titolo misto con due punti alla fine (es: "Premessa:", "Analisi spese:")
    if (/^[A-ZÀÈÌÒÙÉ][\w\s'àèìòùéìù]{3,}:$/.test(trimmed) && trimmed.length <= 60) {
      blocks.push({ type: 'h3', text: trimmed.replace(/:$/, '') });
      continue;
    }

    // Paragrafo normale
    blocks.push({ type: 'paragraph', text: trimmed });
  }

  // Funzione per rendere il testo inline (grassetto **testo**)
  const renderInline = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (/^\*\*[^*]+\*\*$/.test(part)) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  // Raggruppa bullets consecutivi
  type RenderGroup =
    | { kind: 'bullets'; items: string[] }
    | { kind: 'block'; block: Block };

  const groups: RenderGroup[] = [];
  for (const block of blocks) {
    if (block.type === 'bullet') {
      const last = groups[groups.length - 1];
      if (last && last.kind === 'bullets') {
        last.items.push(block.text);
      } else {
        groups.push({ kind: 'bullets', items: [block.text] });
      }
    } else {
      groups.push({ kind: 'block', block });
    }
  }

  return (
    <div className="space-y-3 text-sm leading-relaxed text-foreground/90">
      {groups.map((g, gi) => {
        if (g.kind === 'bullets') {
          return (
            <ul key={gi} className="list-none space-y-1 pl-4">
              {g.items.map((item, ii) => (
                <li key={ii} className="flex items-start gap-2">
                  <span className="text-primary mt-1 flex-shrink-0">▸</span>
                  <span>{renderInline(item)}</span>
                </li>
              ))}
            </ul>
          );
        }

        const { block } = g;

        if (block.type === 'spacer') return <div key={gi} className="h-2" />;

        if (block.type === 'h1') {
          return (
            <div key={gi} className="pt-4 pb-1 border-b-2 border-primary/20">
              <h2 className="text-lg font-bold text-primary uppercase tracking-wide">
                {block.text}
              </h2>
            </div>
          );
        }

        if (block.type === 'h2') {
          return (
            <div key={gi} className="pt-3 pb-0.5">
              <h3 className="text-base font-semibold text-primary border-l-4 border-primary/40 pl-3 py-0.5 bg-primary/5 rounded-r">
                {block.text}
              </h3>
            </div>
          );
        }

        if (block.type === 'h3') {
          return (
            <div key={gi} className="pt-2">
              <h4 className="text-sm font-semibold text-primary/80 uppercase tracking-wider">
                {block.text}
              </h4>
            </div>
          );
        }

        // Paragrafo
        return (
          <p key={gi} className="text-sm leading-7 text-foreground/85">
            {renderInline(block.text)}
          </p>
        );
      })}
    </div>
  );
}

export default function AnalisiDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [analisi, setAnalisi] = useState<Analisi | null>(null);
  const [voci, setVoci] = useState<VoceSpesa[]>([]);
  const [kpiList, setKpiList] = useState<AnalisiKPI[]>([]);
  const [statoPatrimoniale, setStatoPatrimoniale] = useState<StatoPatrimoniale[]>([]);
  const [condomini, setCondomini] = useState<AnalisiCondomino[]>([]);
  const [dettaglioSpese, setDettaglioSpese] = useState<DettaglioSpesa[]>([]);
  const [raccomandazioni, setRaccomandazioni] = useState<Raccomandazione[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchAll = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);

      const [
        { data: analisiData, error: analisiErr },
        { data: vociData },
        { data: kpiData },
        { data: spData },
        { data: condData },
        { data: recData },
      ] = await Promise.all([
        supabase.from('analisi').select('*').eq('id', id).single(),
        supabase.from('voci_spesa').select('*').eq('analisi_id', id),
        supabase.from('analisi_kpi').select('*').eq('analisi_id', id).order('ordine'),
        supabase.from('analisi_stato_patrimoniale').select('*').eq('analisi_id', id).order('ordine'),
        supabase.from('analisi_condomini').select('*').eq('analisi_id', id),
        supabase.from('analisi_raccomandazioni').select('*').eq('analisi_id', id).order('ordine'),
      ]);

      if (analisiErr) {
        setError(`Errore caricamento: ${analisiErr.message}`);
        setLoading(false);
        return;
      }

      // Normalizza: usa nota_sintesi (colonna reale DB) o nota_sintetica (alias)
      if (analisiData) {
        analisiData.nota_sintetica = analisiData.nota_sintetica || analisiData.nota_sintesi || '';
        analisiData.totale_preventivo = analisiData.totale_preventivo ?? 0;
      }
      setAnalisi(analisiData as Analisi);
      setVoci((vociData || []) as VoceSpesa[]);
      setKpiList((kpiData || []) as AnalisiKPI[]);
      setStatoPatrimoniale((spData || []) as StatoPatrimoniale[]);
      setCondomini((condData || []) as AnalisiCondomino[]);
      setDettaglioSpese([]);
      setRaccomandazioni((recData || []) as Raccomandazione[]);

      if (analisiData && (analisiData.stato === 'elaborazione' || analisiData.stato === 'bozza')) {
        startPolling();
      } else {
        stopPolling();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    if (pollingIntervalRef.current) return;
    pollingIntervalRef.current = setInterval(async () => {
      if (!id) return;
      const { data } = await supabase.from('analisi').select('stato').eq('id', id).single();
      if (data && data.stato !== 'elaborazione' && data.stato !== 'bozza') {
        stopPolling();
        fetchAll();
      }
    }, 8000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  useEffect(() => {
    fetchAll();
    return () => {
      stopPolling();
    };
  }, [id]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Caricamento analisi completa...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !analisi) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-4 px-4">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <p className="text-destructive font-semibold text-lg">
            {!analisi && !error ? 'Analisi non trovata' : 'Errore caricamento'}
          </p>
          {error && (
            <p className="text-sm text-muted-foreground bg-muted px-4 py-2 rounded-lg max-w-lg text-center font-mono">
              {error}
            </p>
          )}
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Torna indietro
            </Button>
            <Button onClick={() => window.location.reload()}>Riprova</Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (analisi.stato === 'elaborazione' || analisi.stato === 'bozza') {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-lg font-medium">Analisi in elaborazione...</p>
          <p className="text-sm text-muted-foreground">La pagina si aggiornerà automaticamente.</p>
        </div>
      </DashboardLayout>
    );
  }

  if (analisi.stato === 'errore') {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-4 px-4">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <p className="text-lg font-medium text-destructive">Errore durante l'elaborazione</p>
          {(analisi.nota_sintetica || analisi.nota_sintesi) && (
            <p className="text-sm text-muted-foreground bg-muted px-4 py-2 rounded-lg max-w-lg text-center font-mono">
              {analisi.nota_sintetica || analisi.nota_sintesi}
            </p>
          )}
          <p className="text-sm text-muted-foreground">Vai su Documenti e clicca "Analizza" per ritentare.</p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Torna indietro
            </Button>
            <Button onClick={() => navigate(ROUTE_PATHS.DOCUMENTI)}>Vai a Documenti</Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const macroCategorie = groupVociByCategoria(voci);
  const hasPreventivo = (analisi.totale_preventivo ?? 0) > 0;
  const scostamentoTotale = calcScostamento(analisi.totale_uscite, analisi.totale_preventivo ?? 0);

  // tipo_sp nel DB è 'attivo' o 'passivo'
  const attivita = statoPatrimoniale.filter((v) => v.tipo_sp === 'attivo' || v.tipo_sp === 'attivita' || v.tipo === 'attivita');
  const passivita = statoPatrimoniale.filter((v) => v.tipo_sp === 'passivo' || v.tipo_sp === 'passivita' || v.tipo === 'passivita');
  const totaleAttivita = attivita.reduce((s, v) => s + v.importo, 0);
  const totalePassivita = passivita.reduce((s, v) => s + v.importo, 0);

  const renderNotaIntegrativa = () => {
    const nota = analisi.nota_sintetica || analisi.nota_sintesi || '';
    if (!nota) {
      return <p className="text-muted-foreground italic">Nota integrativa non disponibile.</p>;
    }
    return <NotaRenderer nota={nota} />;
  };

  const raccomandazioneIcon = (priorita: string, tipo?: string) => {
    const v = priorita || tipo || '';
    if (v === 'alta' || v === 'danger') return <AlertCircle className="h-5 w-5 text-destructive" />;
    if (v === 'media' || v === 'warning') return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    return <Info className="h-5 w-5 text-primary" />;
  };

  return (
    <DashboardLayout>
      <style>
        {`
          @media print {
            .no-print { display: none !important; }
            .print\\:rounded-none { border-radius: 0 !important; }
            body { background: white !important; }
            .tabs-content { display: block !important; }
            [role="tabpanel"] { display: block !important; }
          }
        `}
      </style>
      <div className="space-y-6 pb-16 print:pb-0">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl bg-sidebar text-sidebar-foreground p-6 shadow-lg print:rounded-none"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="space-y-1">
              <div className="flex items-center gap-2 mb-2 no-print">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(ROUTE_PATHS.ANALISI)}
                  className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent -ml-2"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
                </Button>
              </div>
              <h1 className="text-2xl font-bold">{analisi.titolo}</h1>
              <p className="text-sidebar-foreground/70 text-sm">
                {analisi.nome_condominio || 'Condominio'}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge className="bg-sidebar-accent text-sidebar-accent-foreground border-sidebar-border text-sm px-3 py-1">
                Esercizio {analisi.anno}
              </Badge>
              <Badge
                className={`text-sm px-3 py-1 ${
                  analisi.stato === 'completata'
                    ? 'bg-success/20 text-success border-success/30'
                    : 'bg-destructive/20 text-destructive border-destructive/30'
                }`}
              >
                {analisi.stato === 'completata' ? 'Completata' : analisi.stato}
              </Badge>
              <div className="flex gap-2 no-print mt-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-sidebar-border text-sidebar-foreground hover:bg-sidebar-accent"
                  onClick={() => navigate(`/analisi/${id}/report`)}
                >
                  <FileDown className="h-4 w-4 mr-1" /> Report Stampa
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-sidebar-border text-sidebar-foreground hover:bg-sidebar-accent"
                  onClick={() => window.print()}
                >
                  <Printer className="h-4 w-4 mr-1" /> Stampa
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            <div className="rounded-lg bg-sidebar-accent p-4">
              <p className="text-xs text-sidebar-foreground/60 uppercase tracking-wider mb-1">Preventivo</p>
              <p className="text-2xl font-mono font-semibold">{formatCurrency(analisi.totale_preventivo)}</p>
              {!hasPreventivo && <p className="text-xs text-sidebar-foreground/40 mt-1">Non disponibile</p>}
            </div>
            <div className="rounded-lg bg-sidebar-accent p-4">
              <p className="text-xs text-sidebar-foreground/60 uppercase tracking-wider mb-1">Consuntivo</p>
              <p className="text-2xl font-mono font-semibold">{formatCurrency(analisi.totale_uscite)}</p>
            </div>
            <div
              className={`rounded-lg p-4 ${
                scostamentoTotale.euro > 0
                  ? 'bg-destructive/30'
                  : scostamentoTotale.euro < 0
                  ? 'bg-success/30'
                  : 'bg-sidebar-accent'
              }`}
            >
              <p className="text-xs text-sidebar-foreground/60 uppercase tracking-wider mb-1">Scostamento</p>
              <p className="text-2xl font-mono font-semibold flex items-center gap-2">
                {scostamentoTotale.euro > 0 ? (
                  <TrendingUp className="h-5 w-5 text-destructive" />
                ) : scostamentoTotale.euro < 0 ? (
                  <TrendingDown className="h-5 w-5 text-success" />
                ) : (
                  <Minus className="h-5 w-5" />
                )}
                {formatCurrency(scostamentoTotale.euro)}
              </p>
              {hasPreventivo && scostamentoTotale.percentuale !== null && (
                <p className="text-sm text-sidebar-foreground/70 mt-1">{formatPct(scostamentoTotale.percentuale)}</p>
              )}
            </div>
          </div>
        </motion.div>

        {kpiList.length > 0 && (
          <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Indicatori di Gestione
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {kpiList.map((kpi) => (
                <KPICard key={kpi.id} kpi={kpi} />
              ))}
            </div>
          </motion.section>
        )}

        {/* KPI morosità sintetici - mostrati se ci sono dati */}
        {(() => {
          const morosi = condomini.filter(c => (c.saldo_finale ?? 0) > 0);
          const totaleMorosita = morosi.reduce((s, c) => s + (c.saldo_finale ?? 0), 0);
          const totaleCrediti = condomini.reduce((s, c) => s + (c.tot_consuntivo ?? c.totale_consuntivo ?? 0), 0);
          const pctMorosita = totaleCrediti > 0 ? (totaleMorosita / totaleCrediti * 100) : 0;
          if (morosi.length === 0) return null;
          return (
            <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <AlertOctagon className="h-5 w-5 text-destructive" />
                Situazione Morosità
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl border-2 border-destructive/40 bg-destructive/10 p-4">
                  <p className="text-xs text-destructive/70 uppercase tracking-wider mb-1">Condomini Morosi</p>
                  <p className="text-2xl font-mono font-bold text-destructive">{morosi.length}</p>
                  <p className="text-xs text-muted-foreground mt-1">su {condomini.length} totali</p>
                </div>
                <div className="rounded-xl border-2 border-destructive/40 bg-destructive/10 p-4">
                  <p className="text-xs text-destructive/70 uppercase tracking-wider mb-1">Rate Non Versate</p>
                  <p className="text-2xl font-mono font-bold text-destructive">{formatCurrency(totaleMorosita)}</p>
                  <p className="text-xs text-muted-foreground mt-1">importo totale insoluto</p>
                </div>
                <div className={`rounded-xl border-2 p-4 ${pctMorosita > 20 ? 'border-destructive bg-destructive/20' : pctMorosita > 5 ? 'border-yellow-500/40 bg-yellow-500/10' : 'border-success/40 bg-success/10'}`}>
                  <p className={`text-xs uppercase tracking-wider mb-1 ${pctMorosita > 20 ? 'text-destructive/70' : pctMorosita > 5 ? 'text-yellow-700' : 'text-success/70'}`}>% su Crediti Totali</p>
                  <p className={`text-2xl font-mono font-bold ${pctMorosita > 20 ? 'text-destructive' : pctMorosita > 5 ? 'text-yellow-600' : 'text-success'}`}>{pctMorosita.toFixed(1)}%</p>
                  <p className="text-xs text-muted-foreground mt-1">su {formatCurrency(totaleCrediti)} totali</p>
                </div>
              </div>
            </motion.section>
          );
        })()}

        <Tabs defaultValue="riepilogo" className="space-y-4">
          <TabsList className="flex flex-wrap h-auto gap-1 no-print">
            <TabsTrigger value="riepilogo" className="flex items-center gap-1">
              <Activity className="h-4 w-4" /> Riepilogo
            </TabsTrigger>
            <TabsTrigger value="grafici" className="flex items-center gap-1">
              <BarChart3 className="h-4 w-4" /> Grafici
            </TabsTrigger>
            <TabsTrigger value="dettaglio" className="flex items-center gap-1">
              <FileText className="h-4 w-4" /> Dettaglio Spese
            </TabsTrigger>
            {condomini.length > 0 && (
              <TabsTrigger value="riparto" className="flex items-center gap-1">
                <Users className="h-4 w-4" /> Riparto Condomini
              </TabsTrigger>
            )}
            {condomini.filter(c => (c.saldo_finale ?? 0) > 0).length > 0 && (
              <TabsTrigger value="morosita" className="flex items-center gap-2">
                <AlertOctagon className="h-4 w-4 text-destructive" />
                <span>Morosità</span>
                <Badge className="bg-destructive text-destructive-foreground text-xs px-1.5 py-0 ml-1">
                  {condomini.filter(c => (c.saldo_finale ?? 0) > 0).length}
                </Badge>
              </TabsTrigger>
            )}
            <TabsTrigger value="nota" className="flex items-center gap-1">
              <FileText className="h-4 w-4" /> Nota Integrativa
            </TabsTrigger>
          </TabsList>

          <TabsContent value="riepilogo" className="space-y-6">
            {/* Riepilogo per macro-categoria */}
            {macroCategorie.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  Riepilogo per Categoria
                </h3>
                <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-sidebar text-sidebar-foreground">
                        <TableHead className="text-sidebar-foreground font-bold">Categoria</TableHead>
                        <TableHead className="text-right text-sidebar-foreground font-bold">Consuntivo</TableHead>
                        {hasPreventivo && <TableHead className="text-right text-sidebar-foreground font-bold">Preventivo</TableHead>}
                        {hasPreventivo && <TableHead className="text-right text-sidebar-foreground font-bold">Scostamento</TableHead>}
                        {hasPreventivo && <TableHead className="text-right text-sidebar-foreground font-bold">%</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {macroCategorie.map((m) => {
                        const scostPct = m.totale_preventivo > 0 ? ((m.totale_consuntivo - m.totale_preventivo) / m.totale_preventivo * 100) : null;
                        return (
                          <TableRow key={m.nome} className="hover:bg-muted/30">
                            <TableCell className="font-semibold text-sm">{m.nome}</TableCell>
                            <TableCell className="text-right font-mono text-sm font-medium">{formatCurrency(m.totale_consuntivo)}</TableCell>
                            {hasPreventivo && <TableCell className="text-right font-mono text-sm text-muted-foreground">{formatCurrency(m.totale_preventivo)}</TableCell>}
                            {hasPreventivo && (
                              <TableCell className={`text-right font-mono text-sm font-semibold ${m.scostamento_euro > 0 ? 'text-destructive' : m.scostamento_euro < 0 ? 'text-success' : ''}`}>
                                {m.scostamento_euro > 0 ? '+' : ''}{formatCurrency(m.scostamento_euro)}
                              </TableCell>
                            )}
                            {hasPreventivo && (
                              <TableCell className={`text-right font-mono text-sm font-semibold ${scostPct !== null && Math.abs(scostPct) > 10 ? (m.scostamento_euro > 0 ? 'text-destructive' : 'text-success') : 'text-muted-foreground'}`}>
                                {formatPct(scostPct)}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                      <TableRow className="bg-sidebar text-sidebar-foreground font-bold border-t-2">
                        <TableCell className="font-bold text-sm">TOTALE GENERALE</TableCell>
                        <TableCell className="text-right font-mono font-bold text-sm">{formatCurrency(analisi.totale_uscite)}</TableCell>
                        {hasPreventivo && <TableCell className="text-right font-mono font-bold text-sm">{formatCurrency(analisi.totale_preventivo)}</TableCell>}
                        {hasPreventivo && (
                          <TableCell className={`text-right font-mono font-bold text-sm ${scostamentoTotale.euro > 0 ? 'text-destructive' : 'text-success'}`}>
                            {scostamentoTotale.euro > 0 ? '+' : ''}{formatCurrency(scostamentoTotale.euro)}
                          </TableCell>
                        )}
                        {hasPreventivo && (
                          <TableCell className={`text-right font-mono font-bold text-sm ${scostamentoTotale.percentuale !== null && Math.abs(scostamentoTotale.percentuale) > 5 ? (scostamentoTotale.euro > 0 ? 'text-destructive' : 'text-success') : ''}`}>
                            {formatPct(scostamentoTotale.percentuale)}
                          </TableCell>
                        )}
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {statoPatrimoniale.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Stato Patrimoniale
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                    <div className="bg-success text-success-foreground px-4 py-3">
                      <h4 className="font-semibold text-sm uppercase tracking-wider">Attività</h4>
                    </div>
                    <Table>
                      <TableBody>
                        {attivita.map((v) => (
                          <TableRow key={v.id} className="hover:bg-muted/30">
                            <TableCell className="text-sm">{v.voce || v.descrizione}</TableCell>
                            <TableCell className="text-right font-mono text-sm font-medium">
                              {formatCurrency(v.importo)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-success/10 border-t-2 border-success/30">
                          <TableCell className="font-bold text-sm">Totale Attività</TableCell>
                          <TableCell className="text-right font-mono font-bold text-sm">
                            {formatCurrency(totaleAttivita)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                    <div className="bg-destructive text-destructive-foreground px-4 py-3">
                      <h4 className="font-semibold text-sm uppercase tracking-wider">Passività</h4>
                    </div>
                    <Table>
                      <TableBody>
                        {passivita.map((v) => (
                          <TableRow key={v.id} className="hover:bg-muted/30">
                            <TableCell className="text-sm">{v.voce || v.descrizione}</TableCell>
                            <TableCell className="text-right font-mono text-sm font-medium">
                              {formatCurrency(v.importo)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-destructive/10 border-t-2 border-destructive/30">
                          <TableCell className="font-bold text-sm">Totale Passività</TableCell>
                          <TableCell className="text-right font-mono font-bold text-sm">
                            {formatCurrency(totalePassivita)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="grafici" className="space-y-6">
            {voci.length === 0 ? (
              <p className="text-muted-foreground text-center py-12">Nessuna voce di spesa disponibile</p>
            ) : (
              <>
                <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                  <h3 className="text-base font-semibold mb-4">Composizione Spese per Categoria</h3>
                  <PieChartCategorie voci={voci} />
                </div>

                {hasPreventivo && macroCategorie.length > 0 && (
                  <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                    <h3 className="text-base font-semibold mb-4">Consuntivo vs Preventivo</h3>
                    <BarChartConfronto voci={voci} />
                  </div>
                )}

                {hasPreventivo && macroCategorie.filter((m) => m.totale_preventivo > 0).length > 0 && (
                  <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                    <h3 className="text-base font-semibold mb-1">Scostamenti % per Categoria</h3>
                    <p className="text-xs text-muted-foreground mb-4">Positivo = sforamento, Negativo = risparmio</p>
                    <BarChartScostamenti voci={voci} />
                  </div>
                )}

                {condomini.length > 0 && (
                  <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                    <h3 className="text-base font-semibold mb-4">Saldo Finale Condomini</h3>
                    <BarChartSaldoCondomini condomini={condomini} />
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="dettaglio">
            <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
              {voci.length === 0 ? (
                <p className="text-muted-foreground text-center py-12">Nessuna voce di spesa</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Categoria</TableHead>
                        <TableHead>Descrizione</TableHead>
                        <TableHead className="text-right">Consuntivo</TableHead>
                        {hasPreventivo && <TableHead className="text-right">Preventivo</TableHead>}
                        {hasPreventivo && <TableHead className="text-right">Scost. €</TableHead>}
                        {hasPreventivo && <TableHead className="text-right">Scost. %</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {macroCategorie.map((macro) => (
                        <>
                          <TableRow key={`macro-${macro.nome}`} className="bg-primary/5 border-t-2 border-primary/20">
                            <TableCell colSpan={2} className="font-bold text-primary text-sm py-2">
                              {macro.nome}
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold text-sm py-2">
                              {formatCurrency(macro.totale_consuntivo)}
                            </TableCell>
                            {hasPreventivo && (
                              <TableCell className="text-right font-mono font-bold text-sm py-2">
                                {formatCurrency(macro.totale_preventivo)}
                              </TableCell>
                            )}
                            {hasPreventivo && (
                              <TableCell
                                className={`text-right font-mono font-bold text-sm py-2 ${
                                  macro.scostamento_euro > 0
                                    ? 'text-destructive'
                                    : macro.scostamento_euro < 0
                                    ? 'text-success'
                                    : ''
                                }`}
                              >
                                {macro.scostamento_euro > 0 ? '+' : ''}
                                {formatCurrency(macro.scostamento_euro)}
                              </TableCell>
                            )}
                            {hasPreventivo && (
                              <TableCell
                                className={`text-right font-mono font-bold text-sm py-2 ${
                                  macro.scostamento_pct !== null && Math.abs(macro.scostamento_pct) > 10
                                    ? macro.scostamento_euro > 0
                                      ? 'text-destructive'
                                      : 'text-success'
                                    : ''
                                }`}
                              >
                                {formatPct(macro.scostamento_pct)}
                              </TableCell>
                            )}
                          </TableRow>
                          {macro.voci.map((voce) => {
                              const cons = voce.importo_consuntivo ?? 0;
                              const prev = voce.importo_preventivo ?? 0;
                              const scostEur = voce.scostamento_euro ?? (cons - prev);
                              const scostPct = voce.scostamento_pct ?? voce.scostamento_percentuale ?? null;
                              return (
                            <TableRow key={voce.id} className="hover:bg-muted/30">
                              <TableCell className="text-xs text-muted-foreground pl-8">{voce.categoria}</TableCell>
                              <TableCell className="text-sm">{voce.descrizione}</TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {formatCurrency(cons)}
                              </TableCell>
                              {hasPreventivo && (
                                <TableCell className="text-right font-mono text-sm text-muted-foreground">
                                  {formatCurrency(prev)}
                                </TableCell>
                              )}
                              {hasPreventivo && (
                                <TableCell
                                  className={`text-right font-mono text-sm ${
                                    scostEur > 0
                                      ? 'text-destructive'
                                      : scostEur < 0
                                      ? 'text-success'
                                      : ''
                                  }`}
                                >
                                  {scostEur > 0 ? '+' : ''}
                                  {formatCurrency(scostEur)}
                                </TableCell>
                              )}
                              {hasPreventivo && (
                                <TableCell
                                  className={`text-right font-mono text-sm ${
                                    scostPct !== null && Math.abs(scostPct) > 10
                                      ? scostEur > 0
                                        ? 'text-destructive font-bold'
                                        : 'text-success font-bold'
                                      : 'text-muted-foreground'
                                  }`}
                                >
                                  {formatPct(scostPct)}
                                </TableCell>
                              )}
                            </TableRow>
                            );
                          })}
                        </>
                      ))}
                      <TableRow className="bg-primary text-primary-foreground font-bold border-t-2">
                        <TableCell colSpan={2} className="font-bold text-sm py-3">
                          TOTALE GENERALE
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold text-sm py-3">
                          {formatCurrency(analisi.totale_uscite)}
                        </TableCell>
                        {hasPreventivo && (
                          <TableCell className="text-right font-mono font-bold text-sm py-3">
                            {formatCurrency(analisi.totale_preventivo)}
                          </TableCell>
                        )}
                        {hasPreventivo && (
                          <TableCell className="text-right font-mono font-bold text-sm py-3">
                            {scostamentoTotale.euro > 0 ? '+' : ''}
                            {formatCurrency(scostamentoTotale.euro)}
                          </TableCell>
                        )}
                        {hasPreventivo && (
                          <TableCell className="text-right font-mono font-bold text-sm py-3">
                            {formatPct(scostamentoTotale.percentuale)}
                          </TableCell>
                        )}
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="riparto">
            <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Nominativo</TableHead>
                      <TableHead className="text-right">Tot. Consuntivo</TableHead>
                      <TableHead className="text-right">Saldo Prec.</TableHead>
                      <TableHead className="text-right">Versamenti</TableHead>
                      <TableHead className="text-right">Saldo Finale</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {condomini.map((c) => (
                      <TableRow
                        key={c.id}
                        className={`hover:bg-muted/30 ${
                          c.saldo_finale > 0 ? 'bg-destructive/5' : c.saldo_finale < 0 ? 'bg-success/5' : ''
                        }`}
                      >
                        <TableCell className="font-medium text-sm flex items-center gap-2">
                          {c.saldo_finale > 0 && <AlertOctagon className="h-3.5 w-3.5 text-destructive flex-shrink-0" />}
                          {c.nominativo}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(c.totale_consuntivo ?? c.tot_consuntivo ?? 0)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(c.saldo_ordinario ?? c.saldo_prec ?? 0)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(c.vers_gest_corrente ?? c.versamenti ?? 0)}
                        </TableCell>
                        <TableCell
                          className={`text-right font-mono text-sm font-bold ${
                            c.saldo_finale > 0 ? 'text-destructive' : c.saldo_finale < 0 ? 'text-success' : ''
                          }`}
                        >
                          {formatCurrency(c.saldo_finale)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* ══════════════════════ TAB MOROSITÀ ══════════════════════ */}
          <TabsContent value="morosita">
            {(() => {
              const morosi = [...condomini]
                .filter(c => (c.saldo_finale ?? 0) > 0)
                .sort((a, b) => (b.saldo_finale ?? 0) - (a.saldo_finale ?? 0));
              const totaleMorosita = morosi.reduce((s, c) => s + (c.saldo_finale ?? 0), 0);
              const totaleCrediti = condomini.reduce((s, c) => s + (c.tot_consuntivo ?? c.totale_consuntivo ?? 0), 0);
              const pctMorosita = totaleCrediti > 0 ? (totaleMorosita / totaleCrediti * 100) : 0;

              if (morosi.length === 0) {
                return (
                  <div className="rounded-xl border border-border bg-card shadow-sm p-12 text-center">
                    <CheckCircle className="h-12 w-12 text-success mx-auto mb-3" />
                    <p className="text-lg font-semibold text-success">Nessun condomino moroso</p>
                    <p className="text-sm text-muted-foreground mt-1">Tutti i condomini risultano in regola con i pagamenti.</p>
                  </div>
                );
              }

              return (
                <div className="space-y-4">
                  {/* Banner riepilogativo */}
                  <div className="rounded-xl border-2 border-destructive/30 bg-destructive/5 p-4">
                    <div className="flex items-start gap-3">
                      <AlertOctagon className="h-6 w-6 text-destructive flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-destructive text-base">Attenzione — Situazione Morosità</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Sono presenti <strong>{morosi.length}</strong> condomini morosi per un totale di{' '}
                          <strong>{formatCurrency(totaleMorosita)}</strong>.
                          Le rate non versate rappresentano il{' '}
                          <strong className={pctMorosita > 20 ? 'text-destructive' : 'text-yellow-600'}>
                            {pctMorosita.toFixed(2)}%
                          </strong>{' '}
                          del totale dei crediti condominiali ({formatCurrency(totaleCrediti)}).
                        </p>
                        {pctMorosita > 10 && (
                          <p className="text-sm text-destructive font-semibold mt-2">
                            ⚠ La percentuale di morosità supera il 10% — si raccomanda azione immediata.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tabella morosi ordinata */}
                  <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                    <div className="bg-destructive text-destructive-foreground px-4 py-3 flex items-center justify-between">
                      <h3 className="font-bold text-sm uppercase tracking-wider">Classifica Morosi (dal più moroso)</h3>
                      <Badge className="bg-white/20 text-white">{morosi.length} condomini</Badge>
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="w-8">#</TableHead>
                            <TableHead>Nominativo</TableHead>
                            <TableHead className="text-right">Quota Dovuta</TableHead>
                            <TableHead className="text-right">Rate Non Versate</TableHead>
                            <TableHead className="text-right">% su Crediti Totali</TableHead>
                            <TableHead className="text-right">Gravità</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {morosi.map((c, idx) => {
                            const dovuto = c.tot_consuntivo ?? c.totale_consuntivo ?? 0;
                            const nonVersate = c.saldo_finale ?? 0;
                            const pctSuTotale = totaleCrediti > 0 ? (nonVersate / totaleCrediti * 100) : 0;
                            const pctSuDovuto = dovuto > 0 ? (nonVersate / dovuto * 100) : 0;
                            const gravita = pctSuDovuto > 75 ? 'critica' : pctSuDovuto > 50 ? 'alta' : pctSuDovuto > 25 ? 'media' : 'bassa';
                            return (
                              <TableRow key={c.id} className="hover:bg-muted/30">
                                <TableCell className="text-xs text-muted-foreground font-mono">{idx + 1}</TableCell>
                                <TableCell className="font-semibold text-sm">{c.nominativo}</TableCell>
                                <TableCell className="text-right font-mono text-sm text-muted-foreground">
                                  {formatCurrency(dovuto)}
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm font-bold text-destructive">
                                  {formatCurrency(nonVersate)}
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm font-semibold">
                                  {pctSuTotale.toFixed(2)}%
                                </TableCell>
                                <TableCell className="text-right">
                                  <Badge className={`text-xs ${
                                    gravita === 'critica' ? 'bg-destructive text-destructive-foreground' :
                                    gravita === 'alta' ? 'bg-orange-500 text-white' :
                                    gravita === 'media' ? 'bg-yellow-500 text-white' :
                                    'bg-muted text-muted-foreground'
                                  }`}>
                                    {gravita.toUpperCase()}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                        <tfoot>
                          <tr className="border-t-2 border-destructive/30 bg-destructive/10">
                            <td className="px-4 py-3 text-sm font-bold" colSpan={3}>TOTALE MOROSITÀ</td>
                            <td className="px-4 py-3 text-right font-mono text-sm font-bold text-destructive">{formatCurrency(totaleMorosita)}</td>
                            <td className="px-4 py-3 text-right font-mono text-sm font-bold">{pctMorosita.toFixed(2)}%</td>
                            <td className="px-4 py-3"></td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  </div>
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="nota">
            <div className="rounded-xl border border-border bg-card shadow-sm">
              <div className="bg-primary text-primary-foreground px-6 py-4 rounded-t-xl">
                <h3 className="font-bold text-base">NOTA INTEGRATIVA E ANALISI DI GESTIONE</h3>
                <p className="text-primary-foreground/70 text-sm mt-0.5">{analisi.titolo}</p>
              </div>
              <div className="p-6 prose prose-sm max-w-none">{renderNotaIntegrativa()}</div>
              {raccomandazioni.length > 0 && (
                <div className="border-t border-border p-6 space-y-3">
                  <h4 className="font-semibold text-base mb-3">Raccomandazioni</h4>
                  {raccomandazioni.map((r) => (
                    <div key={r.id} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/30">
                      {raccomandazioneIcon(r.priorita, r.tipo)}
                      <div className="flex-1">
                        {r.titolo && <p className="text-sm font-semibold mb-1">{r.titolo}</p>}
                        <p className="text-sm leading-relaxed">{r.testo}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t border-border px-6 py-4 text-xs text-muted-foreground">
                Analisi generata il {formatDate(analisi.created_at)} | EgimConsuntivi — Sistema di Analisi Condominiale
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}